import Net from '../../util/net';
import { CommReportRsp, CommReportReq, CommReportDataItem, CommReportDataInfo, SystemType } from '../../typings/proto';
import Cookies from 'js-cookie';
import { env, UrlCommonParam } from '../../util/browser-env';
import { storageKey, StorageManager } from '../storage';
import { UserManager } from '../manager/user';
import { parseQueryString } from '../../util/uri';
import { logger } from '../../util/logger';

export type TReportItemBase = {
  /** 页面id */
  pageId?: string;
  /** 页面位置id */
  posId?: string;
  /** 事件发生时间戳，格式#UNIX时间(精确到毫秒) */
  eventTime?: string;
  /** 停留时长,秒(四舍五入) */
  duration?: string;
  /** 上级来源页id */
  prevId?: string;
  /** 扩展字段1 */
  ext1?: string;
  /** 扩展字段2 */
  ext2?: string;
  /** 扩展字段3 */
  ext3?: string;
  /** 扩展字段4 */
  ext4?: string;
  /** 扩展字段5 */
  ext5?: string;
  /** 扩展字段6 */
  ext6?: string;
  /** 扩展字段7 */
  ext7?: string;
  /** 扩展字段8 */
  ext8?: string;
  /** 扩展字段9 */
  ext9?: string;
  /** 扩展字段10 */
  ext10?: string;
  /** 群id */
  groupId?: string;
  /** 群名称 */
  groupName?: string;
  /** 分类id */
  caid?: string;
  /** 分类名称 */
  cateName?: string;
  /** 频道id */
  channelId?: string;
  /** 频道名称 */
  channelName?: string;
  /** 群组主题标签id */
  themeId?: string;
  /** 群组游戏属性 */
  gameId?: string;
  /** 群组类型(见GroupTemplateType) */
  groupTemplateType?: number;
  /** 会话id */
  conversationId?: string;
  /** 聊天类型(1=群聊,2=私聊,注意,这里和MsgScope枚举值相反!!!) */
  scope?: number;
  /** 消息唯一id */
  messageId?: string;
  /** 消息类型(见MsgL2Type) */
  messageType?: number;
  /** 用户加入群组方式(见GroupJoinType) */
  joinType?: number;
  /** 用户群组身份 */
  roleIdList?: Array<string>;
  /** 接收者uid */
  toUid?: string;
  /** 消息序号,群聊唯一,私聊uid维度唯一 */
  msgSeq?: string;
};

export type TReportItem = TReportItemBase & {
  /** 操作id */
  operId: string
};

export type ReportInitOptions = {
  commonReportData?: Partial<CommReportDataInfo>,
  commonReportDataItem?: Partial<CommReportDataItem>
};

export type TReportOptions = {
  isReportNow?: boolean;
  reportParamsList: TReportItem[];
};

const sessionId = Math.random().toString();
let deviceId = '';

const REPORT_CACHE_SIZE = 10;
const REPORT_CACHE_TIMEOUT = 3000;
const REPORT_CACHE_KEY = 'report_item';

let projectCommonReportData: Partial<CommReportDataInfo> = {};
let projectCommonReportDataItem: Partial<CommReportDataItem> = {};

let commonData: CommReportDataInfo | null = null;

export function initReportOptions(options: ReportInitOptions) {
  projectCommonReportData = options.commonReportData ?? {};
  projectCommonReportDataItem = options.commonReportDataItem ?? {};
  commonData = null;
  // 如果初始化时候，对以前未上报的记录，那么就集体上报一次
  reportFetch();
  setInterval(() => {
    reportFetch();
  }, REPORT_CACHE_TIMEOUT);
}

export function report(params: TReportOptions): void {
  const { isReportNow = false, reportParamsList = [] } = params || {};
  const list = handleReportItem(reportParamsList);
  pushReportItem(list);
  if (isReportNow) {
    reportFetch();
  }
}

function pushReportItem(reportList: Array<CommReportDataItem>) {
  let cacheReportList = StorageManager.getInstance().get<Array<CommReportDataItem>>(REPORT_CACHE_KEY) || [];
  cacheReportList = cacheReportList.concat(reportList);
  StorageManager.getInstance().set(REPORT_CACHE_KEY, cacheReportList);
  // 达到阈值，那么就请看当前队列，并且发送给远端
  if (cacheReportList.length >= REPORT_CACHE_SIZE) {
    reportFetch();
  }
}

export async function reportFetch(): Promise<void> {
  const reportItemList = StorageManager.getInstance().get<Array<CommReportDataItem>>(REPORT_CACHE_KEY) || [];
  if (!reportItemList.length) return;
  StorageManager.getInstance().set(REPORT_CACHE_KEY, []);
  if (!commonData) {
    commonData = await getCommonReportData();
  }
  Net.request<CommReportRsp, CommReportReq>({
    serverName: 'ReportSrv',
    funcName: 'CommReport',
    data: {
      reportData: {
        reportDataInfo: commonData,
        reportDataItemList: reportItemList,
      },
    },
  });
}

function getChannelInfo(): { channel: string, channelName: string } {
  // 小渠道
  let channelName = projectCommonReportDataItem.ext9 ?? '';
  // 大渠道
  let channel = projectCommonReportDataItem.ext10 ?? '';
  if (!channel || !channelName) {
    const urlParams = parseQueryString(location.search);
    if (!channelName) {
      const channelNameParam = urlParams?.[UrlCommonParam.channelName] ?? '';
      if (Array.isArray(channelNameParam)) {
        channelName = channelNameParam?.[0] ?? '';
      } else {
        channelName = channelNameParam ?? '';
      }
    }
    if (!channel) {
      const channelParam = urlParams?.[UrlCommonParam.channel] ?? '';
      if (Array.isArray(channelParam)) {
        channel = channelParam?.[0] ?? '';
      } else {
        channel = channelParam ?? '';
      }
    }
  }
  return {
    channel,
    channelName,
  };
}

function handleReportItem(list: Array<TReportItem>): Array<CommReportDataItem> {
  const result: Array<CommReportDataItem> = [];
  list.forEach((item) => {
    const { operId } = item;
    const defaultOperIdLength = 13;
    if (operId?.length === defaultOperIdLength) {
      const pageId = operId.substring(0, 4);
      const { channel, channelName } = getChannelInfo();
      const toUid = item.toUid || '0';
      const groupId = item.groupId || '0';
      const channelId = item.channelId || '0';
      const eventTime = item.eventTime || Date.now().toString();
      const newObj = {
        pageId,
        ext9: channelName,
        ext10: channel,
        ...item,
        toUid,
        groupId,
        channelId,
        eventTime,
      };
      result.push(newObj as CommReportDataItem);
    } else {
      logger.error('operid不符合格式要求', operId);
    }
  });
  return result;
}

async function getCommonReportData(): Promise<CommReportDataInfo> {
  let sysId = 0; // 1=Android、2=iOS、3=Windows、4=MacOS
  sysId = getSysId();

  const netType = 0; // 网络类型
  if (!deviceId) {
    try {
      const deviceKey = storageKey.getDeviceIdKey();
      const value = localStorage.getItem(deviceKey);
      if (value) {
        deviceId = value;
      } else {
        deviceId = Math.random().toString()
          .replace('0.', '');
        localStorage.setItem(deviceKey, deviceId);
      }
    } catch (e) {
      logger.error('deviceKey localStorage ', e);
    }
  }


  const uid = UserManager.getInstance().getCurrentUid() || '0';
  return {
    platform: 1, // 1=h5\web、2=app、3=小程序、4=pc
    qimei: '', // h5无法获取
    pvid: deviceId, // 前端标识，随机生成一个，当做H5页面中的设备号
    appVersion: env.appVersion, // 闪现的版本号
    sdkVersion: '', // 暂时不报
    ip: '',
    brand: '', // 可从ua中识别
    model: navigator.userAgent,
    sysId, // 1=Android、2=iOS、3=Windows、4=MacOS
    netType, //
    country: '', // 国家，H5不报
    language: navigator.language,
    channel: '', // 渠道包来源， H5不报
    openType: 0, // app启动方式， H5不报
    openid: Cookies.get('openId') || '',
    uid,
    loginType: Number(Cookies.get('loginType')) || 0,
    userStatus: 1, // 	用户状态		1=在线，2=离线，3=勿扰
    userLevel: '', // 预留
    userType: 1, // 1=普通用户，2=机器人，3=虚拟官号
    sessionId, // H5每次打开都随机生成一个， 启动唯一标识session_id，用户在一次会话中的唯一识别ID，判断一天来了这个app几次，每次切到前台都生成1个唯一的id，本次所有行为上报的埋点均上报该ID,
    ...projectCommonReportData,
  };
}

export function getSysId() {
  let sysId = 0;
  if (env.android) {
    sysId = SystemType.SystemTypeAndroid;
  } else if (env.iOS) {
    sysId = SystemType.SystemTypeIOS;
  } else if (env.isWindows) {
    sysId = SystemType.SystemTypeWindows;
  } else if (env.isMac) {
    sysId = SystemType.SystemTypeMacOS;
  }
  return sysId;
}

export async function updateReportCommonData(updateData: Partial<CommReportDataInfo>) {
  if (!commonData) {
    commonData = await getCommonReportData();
  }
  commonData = {
    ...commonData,
    ...updateData,
  };
}
